import { createReducer, isAnyOf } from '@reduxjs/toolkit';
import type {
  AnalyticsTier,
  ContextControl,
  Dashboard,
  FilterControl,
  PeriodControl,
  SettingsControl,
} from 'lib-analytics-types';
import { electron } from 'lib-shared-electron';
import intersectionBy from 'lodash/intersectionBy';
import moment from 'moment-timezone';
import { DeepWritable } from 'ts-essentials';

import { analyticsActions } from './analytics.actions';
import { isOptionalOperator } from './components/controls/utils/filters/isOptionalOperator';

/** Additional metadata to persist in state. */
export type AnalyticsMeta = {
  /** The ISO date when the dashboard data was last refreshed. */
  readonly lastRefresh: string;
  /** Whether to open application links in a new tab. */
  readonly openLinksInNewTab: boolean;
  /** The current tier. */
  readonly tier: AnalyticsTier;
};

export type AnalyticsState = {
  /** The current dashboard. */
  readonly dashboard?: Dashboard;
  /** The selected context for each dashboard. */
  readonly context: Record<Dashboard['id'], ContextControl>;
  /** The selected period filter. */
  readonly period: PeriodControl;
  /** The selected instrument filters. */
  readonly filters: readonly FilterControl[];
  /** The dashboard settings. */
  readonly settings: SettingsControl;
  /** The object containing metadata. */
  readonly meta: AnalyticsMeta;
  /** The current quick view state. */
  readonly quickView?: {
    /** The id of the current or last visited quick view. */
    readonly id: string;
  };
};

export const initialState: AnalyticsState = {
  dashboard: undefined,
  context: {},
  period: {
    field: 'created_at',
    values: [{ value: 'today' }],
  },
  filters: [],
  settings: {
    timezone: moment.tz.guess(),
  },
  meta: {
    lastRefresh: new Date().toISOString(),
    openLinksInNewTab: 'SharedWorker' in window && !electron.isElectron(),
    tier: 'analytics',
  },
};

export const analyticsReducer = createReducer(initialState, (builder) =>
  builder
    .addCase(
      analyticsActions.setDashboard,
      (state, { payload: { dashboard } }) => {
        state.dashboard = dashboard as DeepWritable<typeof dashboard>;

        // Add the default context for the dashboard, if the dashboards supports context and a
        // previously selected context doesn't already exist in the state.
        if (dashboard.context && !state.context[dashboard.id]) {
          const values = dashboard.context.values
            .filter(({ preselected }) => preselected)
            .map(({ value }) => ({ value })) as DeepWritable<
            (typeof state.context)[number]['values']
          >;

          state.context[dashboard.id] = {
            field: dashboard.context.field,
            values,
            operator: 'is_one',
          };
        }

        // Some dashboards might have some filters applied that aren't expected in other dashboards, so
        // we remove them when switching dashboards.
        state.filters.forEach((filter, i) => {
          const dashboardFilter = dashboard.filters.find(
            ({ field }) => field === filter.field,
          );

          // The next dashboard does not support the filter, removing it from the state.
          if (!dashboardFilter) {
            state.filters.splice(i, 1);
            return;
          }

          // The next dashboard supports the filter, but some selected values might be obsolete.
          if (filter.field !== 'custom_fields') {
            filter.values = dashboardFilter.values.length
              ? (intersectionBy(
                  dashboardFilter.values.map(({ value }) => ({ value })),
                  filter.values,
                  'value',
                ) as DeepWritable<typeof filter.values>)
              : filter.values;
          }

          // If no values remain selected, and the operator is not a optional operator, remove the filter.
          if (!filter.values.length && !isOptionalOperator(filter.operator)) {
            state.filters.splice(i, 1);
          }
        });

        // Set the tier corresponding to the current dashboard.
        state.meta.tier = dashboard.tier ?? 'analytics';

        // Remove the last visited quick view navigating to a dashboard.
        delete state.quickView;
      },
    )
    .addCase(analyticsActions.setContext, (state, { payload: { context } }) => {
      if (state.dashboard) {
        state.context[state.dashboard.id] = {
          ...context,
          values: [...context.values],
        };
      }
    })
    .addCase(analyticsActions.setPeriod, (state, { payload }) => {
      state.period = payload.period as DeepWritable<typeof payload.period>;
    })
    .addCase(analyticsActions.setFilters, (state, { payload }) => {
      state.filters = payload.filters as DeepWritable<typeof payload.filters>;
    })
    .addCase(analyticsActions.setSettings, (state, { payload }) => {
      state.settings = { ...state.settings, ...payload.settings };

      // The period may have been automatically adjusted from a timezone change. If so, update it.
      if (payload.period) {
        state.period = payload.period as DeepWritable<typeof payload.period>;
      }
    })
    .addCase(analyticsActions.setMeta, (state, { payload }) => {
      state.meta = { ...state.meta, ...payload.meta };
    })

    .addCase(analyticsActions.setQuickView, (state, { payload: { id } }) => {
      state.quickView = state.quickView ?? { id };
      state.quickView.id = id;
    })

    // Updates the last refresh timestamp when the dashboard changes, or when any of the
    // user-configurable data is updated, such as the dashboard context, period or filters. Once the
    // timestamp is updated, the filters data and the instrument data in each panel is refreshed.
    .addMatcher(
      isAnyOf(
        analyticsActions.setDashboard,
        analyticsActions.setQuickView,
        analyticsActions.setContext,
        analyticsActions.setPeriod,
        analyticsActions.setFilters,
        analyticsActions.setSettings,
        analyticsActions.forceRefresh,
      ),
      (state) => {
        state.meta.lastRefresh = new Date().toISOString();
      },
    ),
);
